import { useState } from 'react';

const FORM_ACTION = 'https://readdy.ai/api/form/d717ul9lum2co2q1fhug';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });
      const res = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-white py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left */}
          <div>
            <span className="px-4 py-1 rounded-full border border-stone-300 text-stone-500 text-xs tracking-widest uppercase mb-6 inline-block">
              Kontakt
            </span>
            <h2
              className="font-light text-stone-900 leading-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontFamily: "'Playfair Display', serif" }}
            >
              Ihr Projekt beginnt<br />
              <em className="not-italic text-stone-400">mit einem Gespräch.</em>
            </h2>
            <p className="text-stone-500 text-sm leading-relaxed mb-12">
              Gern erstellen wir Ihnen ein individuelles Angebot — genau nach Ihren Wünschen und Bedürfnissen. Wir freuen uns auf Ihre Nachricht!
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-2-line text-stone-600 text-base" />
                </div>
                <div>
                  <p className="text-xs text-stone-400 uppercase tracking-widest mb-1">Adresse</p>
                  <p className="text-sm text-stone-700 leading-relaxed">
                    Ziersteine.de – Natursteine aus aller Welt<br />
                    Rostocker Landstrasse 3a<br />
                    18184 Broderstorf / OT Neu Pastow
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-stone-600 text-base" />
                </div>
                <div>
                  <p className="text-xs text-stone-400 uppercase tracking-widest mb-1">E-Mail</p>
                  <a
                    href="mailto:ziersteine@spickermann-gmbh.net"
                    className="text-sm text-stone-700 hover:text-stone-500 transition-colors"
                  >
                    ziersteine@spickermann-gmbh.net
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-stone-600 text-base" />
                </div>
                <div>
                  <p className="text-xs text-stone-400 uppercase tracking-widest mb-1">Telefon</p>
                  <a
                    href="tel:+4938204693597"
                    className="text-sm text-stone-700 hover:text-stone-500 transition-colors"
                  >
                    038204 / 693597
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-stone-50 rounded-2xl p-8 lg:p-10">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-14 h-14 flex items-center justify-center bg-stone-900 rounded-full mb-5">
                  <i className="ri-check-line text-white text-xl" />
                </div>
                <h3 className="text-stone-900 font-semibold text-lg mb-2">Vielen Dank!</h3>
                <p className="text-stone-500 text-sm leading-relaxed max-w-xs">
                  Ihre Nachricht wurde erfolgreich übermittelt. Wir melden uns so bald wie möglich bei Ihnen.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2 bg-stone-900 text-white rounded-full text-sm cursor-pointer hover:bg-stone-700 transition-colors whitespace-nowrap"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-stone-500 uppercase tracking-widest mb-1.5">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Ihr vollständiger Name"
                      className="w-full bg-white border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 uppercase tracking-widest mb-1.5">E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="ihre@email.de"
                      className="w-full bg-white border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-stone-500 uppercase tracking-widest mb-1.5">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Ihre Telefonnummer"
                      className="w-full bg-white border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 uppercase tracking-widest mb-1.5">Betreff</label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full bg-white border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-stone-400 transition-colors cursor-pointer"
                    >
                      <option value="">Bitte auswählen</option>
                      <option value="Edelsplitt">Edelsplitt</option>
                      <option value="Gabionensteine">Gabionensteine</option>
                      <option value="Dekosteine">Dekosteine</option>
                      <option value="Zubehör">Zubehör</option>
                      <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-stone-500 uppercase tracking-widest mb-1.5">Nachricht *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                    className="w-full bg-white border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors resize-none"
                  />
                  <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-red-400' : 'text-stone-400'}`}>
                    {charCount} / 500
                  </div>
                </div>
                {status === 'error' && (
                  <p className="text-red-500 text-xs">
                    Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading' || charCount > 500}
                  className="w-full bg-stone-900 text-white rounded-full py-3 text-sm font-medium hover:bg-stone-700 transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {status === 'loading' ? 'Wird gesendet...' : 'Anfrage absenden'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
